/* eslint-disable no-undef */
import "./index.scss";
import { useCallback, useRef, useState } from "react";
import { NavLink } from "hooks/useCustomNavigation";
import { Button } from "components/UI/atoms";
import { nameToSlug } from "utils/strings";
import { useSellSearch } from "./utils/useSellSearch";
import { SellSearchResults } from "components/UI/molecules";

const DEBOUNCE_DELAY = 1000;

export const SellSearch = () => {
  const {
    searchResults,
    searchSuggestions,
    error,
    searchByName,
    setSearchResults,
    setSearchSuggestions,
    submitValue,
    setError
  } = useSellSearch();
  const [searchValue, setSearchValue] = useState("");
  const debounceTimeout = useRef(null);
  const inputRef = useRef(null);

  const handleInputChange = ({ target: { value } }) => {
    setSearchValue(value);
    setError(false);

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    if (!value.length) {
      setSearchResults([]);
      setSearchSuggestions([]);
      return;
    }

    debounceTimeout.current = setTimeout(() => {
      searchByName(value);
      debounceTimeout.current = null;
    }, DEBOUNCE_DELAY);
  };

  const handleInputBlur = (event) => {
    const isResultList = event.currentTarget.contains(event.relatedTarget);
    if (!isResultList) return setSearchResults([]);

    inputRef.current.focus();
  };

  const handleInputSubmit = (event) => {
    event.preventDefault();
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    submitValue(searchValue);
  };

  const handleSearchItemFocus = useCallback(
    (name, emptyResults) => {
      if (name) setSearchValue(name);
      if (emptyResults) setSearchResults([]);
    },
    [setSearchResults]
  );

  const shouldDisplaySuggestionsBar = !!searchSuggestions.length || error;

  return (
    <div className="sell-search">
      <form
        className="search-container"
        onSubmit={handleInputSubmit}
        onBlur={handleInputBlur}
      >
        <div className={`input-wrapper ${error && "error"}`}>
          <input
            placeholder="Enter store name"
            onChange={handleInputChange}
            onSubmit={handleInputSubmit}
            value={searchValue}
            ref={inputRef}
            type="text"
            autoFocus
          />
          {!!error && !searchValue.length && (
            <p className="required-text">This field is required.</p>
          )}
          {!!searchResults.length && (
            <SellSearchResults
              list={searchResults}
              onSearchItemFocus={handleSearchItemFocus}
            />
          )}
        </div>
        <Button customClassName="submit-button" onClick={handleInputSubmit}>
          Get started
        </Button>
      </form>
      {shouldDisplaySuggestionsBar && (
        <div className="search-suggestions">
          {searchSuggestions.length ? (
            <p>
              Did you Mean:{" "}
              {searchSuggestions.map(({ value, name }, index) => {
                const isLast = index === searchSuggestions.length - 1;
                const linkTo = `/sell-${nameToSlug(name)}-gift-cards`;

                return (
                  <span key={linkTo}>
                    <NavLink to={linkTo}>{value}</NavLink>
                    {!isLast && ", "}
                  </span>
                );
              })}
            </p>
          ) : (
            <p>No brands found.</p>
          )}
        </div>
      )}
    </div>
  );
};
